import { combineEpics, ofType } from 'redux-observable';
import { EMPTY } from 'rxjs';
import { concatMap } from 'rxjs/operators';
import { postPunchoutOrderPrivateMethod } from '../../generated/api/uiApiMethods.js';
import { TypeKeys } from '../actions/index.js';
import { submitPunchoutOrderFailed, submitPunchoutOrderFulfilled } from '../actions/punchoutActions.js';
import {
  clearShoppingCart,
  removePrimaryMdmId,
  removePunchoutDeviceChangeRequest,
} from '../common/localStorageUtils.js';
import { callUiApi, prepareUiApiRequest } from '../common/uiApiUtils.js';
import { actionToActionState } from './epicUtils.js';
import type { ActionAndState, EpicDependencies } from './epicUtils.js';
import type { PunchoutOrderResponse } from '../../common/utils/punchoutUtils.js';
import type { ActionTypes, InitiateEmployeeOnBoardingAction } from '../actions/index.js';
import type { SubmitPunchoutOrderAction, SubmitPunchoutOrderFulfilledAction } from '../actions/punchoutActions.js';
import type { State } from '../common/store.js';
import type { Action } from 'redux';
import type { ActionsObservable, Epic, StateObservable } from 'redux-observable';
import type { AjaxResponse } from 'rxjs/ajax';

export const submitPunchoutOrderEpic: Epic<ActionTypes, Action, State, EpicDependencies> = (
  action$: ActionsObservable<ActionTypes>,
  state$: StateObservable<State>,
  epicDependencies: EpicDependencies
) =>
  prepareUiApiRequest(
    action$.pipe(ofType(TypeKeys.SUBMIT_PUNCHOUT_ORDER)),
    (action: InitiateEmployeeOnBoardingAction) => actionToActionState(action, state$, 'punchoutOrders')
  ).pipe(
    concatMap((actionAndState: ActionAndState) => {
      const punchoutOrderAction = actionAndState.action as SubmitPunchoutOrderAction;
      return callUiApi({
        epicDependencies,
        state$,
        method: postPunchoutOrderPrivateMethod(),
        payload: punchoutOrderAction.order,
        successAction: (ajaxResponse: AjaxResponse) =>
          submitPunchoutOrderFulfilled(ajaxResponse.response as PunchoutOrderResponse),
        failureAction: submitPunchoutOrderFailed,
      });
    })
  );

export const submitPunchoutOrderFulfilledEpic: Epic<ActionTypes, Action, State, EpicDependencies> = (
  action$: ActionsObservable<ActionTypes>
) =>
  action$.ofType(TypeKeys.SUBMIT_PUNCHOUT_ORDER_FULFILLED).pipe(
    concatMap((action: SubmitPunchoutOrderFulfilledAction) => {
      const form = document.createElement('form');
      form.setAttribute('hidden', 'true');
      form.setAttribute('method', 'POST');
      form.setAttribute('action', action.response.url);
      Object.keys(action.response.data).forEach(key => {
        const input1 = document.createElement('input');
        input1.setAttribute('type', 'text');
        input1.setAttribute('name', key);
        input1.setAttribute('value', action.response.data[key]);
        form.appendChild(input1);
      });
      document.body.appendChild(form);
      form.submit();
      removePunchoutDeviceChangeRequest();
      removePrimaryMdmId();
      clearShoppingCart();
      return EMPTY;
    })
  );

export const punchoutEpic: Epic<ActionTypes, Action, State, EpicDependencies> = combineEpics(
  submitPunchoutOrderEpic,
  submitPunchoutOrderFulfilledEpic
);
